import { useEffect, useState } from "react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Legend, CartesianGrid } from "recharts";
import { PageHeader } from "@/components/Layout";
import { api } from "@/lib/api";
import { BestTimes } from "@/components/BestTimes";

const pct = (n, d) => (d ? `${Math.round((n / d) * 100)}%` : "—");

function Kpi({ label, value, sub, testid }) {
  return (
    <div className="bg-white border rounded-xl p-4" data-testid={testid}>
      <div className="label-xs">{label}</div>
      <div className="text-3xl font-extrabold tracking-tight mt-1">{value ?? 0}</div>
      {sub && <div className="text-xs text-slate-500 mt-0.5">{sub}</div>}
    </div>
  );
}

function Smtp2goSummary() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/analytics/smtp2go").then((r) => setD(r.data)); }, []);
  if (!d) return null;
  return (
    <div className="bg-white border rounded-xl p-5" data-testid="smtp2go-summary">
      <h3 className="font-bold mb-3">SMTP2Go account summary</h3>
      {!d.configured && <p className="text-sm text-slate-500">Connect SMTP2Go in Settings to see account-level delivery stats.</p>}
      {d.error && <p className="text-sm text-red-600">{d.error}</p>}
      {d.summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          {Object.entries(d.summary).filter(([, v]) => typeof v !== "object").map(([k, v]) => (
            <div key={k} className="bg-slate-50 rounded-lg p-2.5"><div className="label-xs !text-[9px]">{k.replace(/_/g, " ")}</div><div className="font-mono text-sm font-semibold">{String(v)}</div></div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function Analytics() {
  const [days, setDays] = useState("30");
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/analytics", { params: { days } }).then((r) => setD(r.data)); }, [days]);
  const t = d?.totals || {};
  return (
    <div>
      <PageHeader eyebrow="Email performance" title="Analytics">
        <Select value={days} onValueChange={setDays}><SelectTrigger className="w-40" data-testid="analytics-range"><SelectValue /></SelectTrigger>
          <SelectContent>{["7", "30", "90", "365"].map((x) => <SelectItem key={x} value={x}>Last {x} days</SelectItem>)}</SelectContent></Select>
      </PageHeader>
      <div className="p-6 lg:p-10 space-y-6">
        <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-8 gap-3 fade-up">
          <Kpi label="Sent" value={t.sent} testid="kpi-sent" />
          <Kpi label="Delivered" value={t.delivered} sub={pct(t.delivered, t.sent)} testid="kpi-delivered" />
          <Kpi label="Opened" value={t.opened} sub={pct(t.opened, t.sent)} testid="kpi-opened" />
          <Kpi label="Clicked" value={t.clicked} sub={pct(t.clicked, t.sent)} testid="kpi-clicked" />
          <Kpi label="Page visits" value={t.visited} sub={pct(t.visited, t.sent)} testid="kpi-visited" />
          <Kpi label="Replies" value={t.replies} testid="kpi-replies" />
          <Kpi label="Bounced" value={t.bounced} sub={pct(t.bounced, t.sent)} testid="kpi-bounced" />
          <Kpi label="Unsub / spam" value={(t.unsubscribed || 0)} sub={`${t.spam || 0} spam`} testid="kpi-unsub" />
        </div>
        <div className="bg-white border rounded-xl p-5" data-testid="analytics-daily-chart">
          <h3 className="font-bold mb-3">Daily activity</h3>
          <div style={{ height: 280 }}>
            <ResponsiveContainer>
              <BarChart data={d?.daily || []}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="date" tick={{ fontSize: 11 }} /><YAxis allowDecimals={false} tick={{ fontSize: 11 }} /><Tooltip /><Legend />
                <Bar dataKey="sent" fill="#2563EB" radius={[3, 3, 0, 0]} /><Bar dataKey="open" fill="#F59E0B" radius={[3, 3, 0, 0]} />
                <Bar dataKey="click" fill="#F97316" radius={[3, 3, 0, 0]} /><Bar dataKey="visit" fill="#10B981" radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
        <BestTimes />
        <div className="bg-white border rounded-xl overflow-x-auto" data-testid="analytics-campaign-table">
          <table className="w-full text-sm">
            <thead><tr className="bg-slate-50 border-b text-left">{["Campaign", "Sent", "Delivered", "Open rate", "Click rate", "Visit rate", "Bounced", "Spam"].map((h) => <th key={h} className="p-3 label-xs">{h}</th>)}</tr></thead>
            <tbody>
              {(d?.by_campaign || []).map((r) => (
                <tr key={r.campaign_id || "none"} className="border-b last:border-0">
                  <td className="p-3 font-semibold">{r.campaign}</td><td className="p-3">{r.sent}</td><td className="p-3">{r.delivered}</td>
                  <td className="p-3">{pct(r.opened, r.sent)}</td><td className="p-3">{pct(r.clicked, r.sent)}</td><td className="p-3">{pct(r.visited, r.sent)}</td>
                  <td className="p-3">{r.bounced}</td><td className="p-3">{r.spam}</td>
                </tr>))}
              {!d?.by_campaign?.length && <tr><td colSpan={8} className="p-8 text-center text-slate-500">No emails sent in this period.</td></tr>}
            </tbody>
          </table>
        </div>
        <p className="text-xs text-slate-500">Opens/clicks combine Sales Navigator's built-in tracking with SMTP2Go webhook events (de-duplicated). Delivered, bounce and spam data require the SMTP2Go webhook ({d?.webhook_events ?? 0} webhook events received in this period). Opens are estimates — Apple Mail Privacy and image blocking affect them.</p>
        <Smtp2goSummary />
      </div>
    </div>
  );
}
